import type { CheckoutSessionResponse } from "../api/checkout";
import { networkDisplayName, originBlockchainLabel } from "./networkLabels";

/**
 * What the payer's own leg of a session looks like.
 *
 * A swap-funded session stores the DESTINATION leg — `token`, `network`,
 * `amountRequired` and `amountReceived` are all mainnet Solana USDC, which the
 * payer never sees and never touches. They were told to send some amount of
 * their own token on their own chain, and every payer-facing figure has to be
 * that one.
 *
 * Extracted rather than repeated: the payment screen already substituted these
 * values inline while the confirmation screen did not, so the two screens
 * disagreed about what the payer had just paid with. One helper is what stops
 * that recurring.
 */
export interface SessionPayDisplay {
  /** True when the payer funded this through a swap. */
  isSwap: boolean;
  /**
   * Human-unit amount in the payer's own token.
   *
   * `null` only when the origin token's decimals could not be resolved — a
   * real state that must render as "unavailable", never as a fabricated or
   * substituted number. A payer sending the wrong amount cannot top up: the
   * quote-bound deposit address is spent once.
   */
  amount: string | null;
  /** The payer's own token symbol, e.g. `TRUMP` — never the destination's. */
  symbol: string;
  /** The payer's own chain, e.g. `Solana` — never the destination's. */
  chainLabel: string;
}

export function sessionPayDisplay(
  session: CheckoutSessionResponse,
): SessionPayDisplay {
  const swap = session.swap;
  if (swap === null) {
    return {
      isSwap: false,
      amount: String(session.amountRequired),
      symbol: session.token,
      chainLabel: networkDisplayName(session.network),
    };
  }
  return {
    isSwap: true,
    // `maxAmountIn` is the worst-case input the quote is bound to, already in
    // human origin units. Never a smaller figure — a short send cannot be
    // topped up and leaves the payer unable to complete the order.
    amount: swap.maxAmountIn,
    symbol: swap.originSymbol,
    chainLabel: originBlockchainLabel(swap.originBlockchain),
  };
}
